import { useCallback, useLayoutEffect, useRef } from 'react'
import { editNumericValueAtCaret } from '@utils/caretEdit'

type Options = {
  maxLength: number
  stripLeadingZeros?: boolean
}

/**
 * Wires a custom on-screen NumericKeyboard to a text `<input>` so edits happen
 * at the current caret/selection instead of always at the tail.
 *
 * Returns an `inputRef` to attach to the underlying `<input>` and a
 * `handleKeyboardChange` to pass to `NumericKeyboard`'s `onChange`. After each
 * edit the caret is restored via `setSelectionRange` once the DOM has the new
 * value (the input is controlled, so React resets the caret to the end on
 * re-render without this).
 */
export function useKeypadCaret(
  value: string,
  setValue: (next: string) => void,
  { maxLength, stripLeadingZeros = false }: Options
) {
  const inputRef = useRef<HTMLInputElement>(null)
  const pendingCaretRef = useRef<number | null>(null)

  useLayoutEffect(() => {
    if (pendingCaretRef.current !== null && inputRef.current) {
      const pos = pendingCaretRef.current
      inputRef.current.setSelectionRange(pos, pos)
      pendingCaretRef.current = null
    }
  }, [value])

  const handleKeyboardChange = useCallback(
    (key: string | 'delete') => {
      const el = inputRef.current
      const start = el?.selectionStart ?? value.length
      const end = el?.selectionEnd ?? start
      const result = editNumericValueAtCaret({
        value,
        selection: { start, end },
        key,
        maxLength,
        stripLeadingZeros,
      })

      if (result.value === value) {
        // No change (cap reached / delete at start) — just keep the caret put.
        if (el) {
          const pos = result.selection.start
          el.setSelectionRange(pos, pos)
        }
        return
      }

      pendingCaretRef.current = result.selection.start
      setValue(result.value)
    },
    [value, setValue, maxLength, stripLeadingZeros]
  )

  return { inputRef, handleKeyboardChange }
}
